import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { getResult } from "../../../client-shared/api/services.utils";
import { services } from "../../../globals";
import { useSuppressedFilter } from "../../../state/FilterContext";
import { useSiteContext } from "../../../state/SiteContext";
import { TextExtractor, findFirstComponentOfType } from "../../internal/TextExtractor";

interface PathHasMarkdownChunksProps {
    children: ReactNode,
    suppress?: string,
}

/***
 * Value source that outputs plain text `true` or `false` depending on whether
 * indexed markdown chunks exist for the given path and validity.
 */
export const PathHasMarkdownChunks = React.memo(function PathHasMarkdownChunks({
    children,
    suppress,
}: PathHasMarkdownChunksProps): React.ReactElement {
    const linkedState = useSuppressedFilter(suppress || "");
    const { siteState } = useSiteContext();
    const [pathValue, setPathValue] = useState("");
    const [exists, setExists] = useState(false);
    const pathChild = useMemo(() => findFirstComponentOfType(children, "arg-path"), [children]);

    useEffect(() => {
        let cancelled = false;
        const checkExists = async () => {
            if (!pathValue || !siteState.site?.name) {
                if (!cancelled) {
                    setExists(false);
                }
                return;
            }
            const url = services.file.getMarkdownExistsUrl(
                siteState.site.name,
                pathValue,
                linkedState.validity || "",
                linkedState.systemProperties || {},
            );
            try {
                const res = await fetch(url, { credentials: "include" });
                const body = await getResult(res) as { exists?: boolean } | null;
                if (!cancelled) {
                    setExists(body?.exists === true);
                }
            } catch {
                if (!cancelled) {
                    setExists(false);
                }
            }
        };
        void checkExists();
        return () => {
            cancelled = true;
        };
    }, [
        pathValue,
        siteState.site?.name,
        linkedState.validity,
        linkedState.systemProperties,
    ]);

    return <>
        {pathChild && (
            <TextExtractor onExtractedText={setPathValue}>
                {pathChild?.props.children}
            </TextExtractor>
        )}
        {exists ? "true" : "false"}
    </>;
});
